import React, { createContext, useContext, useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import api from '../api/axiosConfig';

const AccountingContext = createContext();

export const useAccounting = () => useContext(AccountingContext);

export const AccountingProvider = ({ children }) => {
    const [stats, setStats] = useState({
        totalIncome: 0,
        totalExpense: 0,
        pendingReceivables: 0,
        pendingPayables: 0
    });
    const [loading, setLoading] = useState(false);

    const apiCall = async (endpoint, method = 'GET', body = null) => {
        setLoading(true);
        try {
            const response = await api({
                url: `/accounting${endpoint}`,
                method,
                data: body || undefined
            });
            const data = response.data?.data !== undefined ? response.data.data : response.data;
            return { success: true, data };
        } catch (error) {
            console.error("Accounting API Error:", error);
            const msg = error.response?.data?.error || error.message;
            toast.error(`System Error: ${msg}`);
            return { success: false, error: msg };
        } finally {
            setLoading(false);
        }
    };

    // --- ACTIONS ---

    const fetchStats = useCallback(async () => {
        const res = await apiCall('/stats');
        if (res.success && res.data) setStats(res.data);
    }, []);

    const createInvoice = async (invoiceData) => {
        const res = await apiCall('/invoices', 'POST', invoiceData);
        if (res.success) {
            toast.success("Invoice Created Successfully!");
            fetchStats(); 
        }
        return res;
    };

    const approveInvoice = async (id) => {
        const res = await apiCall(`/invoices/${id}/approve`, 'PUT');
        if (res.success) {
            toast.success("Invoice Posted to Ledger!");
            fetchStats();
        }
        return res;
    };

    const createVendorBill = async (billData) => {
        const res = await apiCall('/bills', 'POST', billData);
        if (res.success) {
            toast.success("Vendor Bill Recorded!");
            fetchStats();
        }
        return res;
    };

    // 🟢 Deep Link Fetch Functions
    const fetchVendorBillById = async (id) => {
        const res = await apiCall(`/bills/${id}`);
        return res.success ? res.data : null;
    };

    const fetchInvoiceById = async (id) => {
        const res = await apiCall(`/invoices/${id}`);
        return res.success ? res.data : null;
    };

    const value = {
        stats,
        loading,
        fetchStats,
        createInvoice,
        approveInvoice,
        createVendorBill,
        fetchVendorBillById,
        fetchInvoiceById
    };

    return (
        <AccountingContext.Provider value={value}>
            {children}
        </AccountingContext.Provider>
    );
};